/**
 * Open or focus the data-profile workspace tab for a given table or column.
 *
 * If a tab for the same connection/database/schema/table already exists, we
 * focus it and (optionally) update its `focusedColumn`. Otherwise we add a
 * fresh tab with `dataMode: 'server'` (the default — accurate aggregation).
 *
 * Also fires the `feature:data_profile_open` analytics event with categorical
 * payload only (engine, entry point, data mode, whether a focused column was
 * passed). No table or column names are sent.
 */

import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { isDataProfileTab } from '../../core/types/workspaceTypes';
import { lookupEngine, trackDataProfileEvent } from './dataProfileAnalytics';

export type DataProfileEntryPoint =
  | 'toolbar'
  | 'object_explorer_table'
  | 'object_explorer_column'
  | 'grid_header'
  | 'grid_cell'
  | 'popover_footer'
  | 'table_explorer_grid';

export type OpenDataProfileTabContext = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  focusedColumn?: string;
  /** Override the default 'server' mode (e.g. 'sample' from grid context menus). */
  dataMode?: 'server' | 'sample';
  /** Where the user invoked Profile Data from. Categorical, no PII. */
  entryPoint: DataProfileEntryPoint;
};

export function openDataProfileTab(context: OpenDataProfileTabContext): void {
  const { tabs, addTab, setActiveTab, updateTabContent } = useWorkspaceStore.getState();
  const dataMode = context.dataMode ?? 'server';

  const engine = lookupEngine(context.connectionId);

  trackDataProfileEvent('feature:data_profile_open', {
    entryPoint: context.entryPoint,
    dataMode,
    hasFocusedColumn: !!context.focusedColumn,
    engine,
  });

  const existing = tabs.find(
    (t) =>
      isDataProfileTab(t) &&
      t.content.connectionId === context.connectionId &&
      t.content.database === context.database &&
      t.content.schema === context.schema &&
      t.content.table === context.table,
  );

  if (existing) {
    if (context.focusedColumn && existing.type === 'data-profile') {
      updateTabContent<'data-profile'>(existing.id, {
        focusedColumn: context.focusedColumn,
      });
    }
    setActiveTab(existing.id);
    return;
  }

  const title = `Profile · ${context.schema ? `${context.schema}.${context.table}` : context.table}`;
  addTab({
    type: 'data-profile',
    title,
    connectionInfo: {
      connectionId: context.connectionId,
      database: context.database,
    },
    content: {
      connectionId: context.connectionId,
      database: context.database,
      schema: context.schema,
      table: context.table,
      focusedColumn: context.focusedColumn,
      dataMode,
    },
  });
}
